<script setup>
/**
 * FormControlModulestructureTableTargetColumn
 * Composition-based wrapper around the standard select dropdown form control.
 * Parses cross-component relationship schemas to formulate unified relational option links,
 * automatically dropping its visual layout visibility if the current property type is not 'table'.
 */
import { computed, onMounted, getCurrentInstance } from 'vue';
import FormControlTypeSelect from '../form-control-type/FormControlTypeSelect.vue';

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

/**
 * Aggregates, filters, and formats cross-referenced entity relationships.
 * Isolates structure matching the exact module ID and extracts associated
 * component headers to generate a descriptive picklist array structure.
 */
const options = computed(() => {
    const structureItems = store.state.componentStructure?.items || [];
    const componentItems = store.state.components?.items || [];
    const targetModuleId = props.currentData?.modulecomponentsID || props.currentData?.modulecomponentsId;

    // Filter structural parameters checking module targeting and relationship maps
    const filteredStructures = structureItems.filter((obj) => {
        return obj.categoryComponentId === targetModuleId && obj.type === 'relationshipOneToMany';
    });

    // Formulate descriptive picklist records safely
    const selectOptions = [];

    filteredStructures.forEach((item) => {
        const component = componentItems
            .find(obj => obj.id === item.modulecomponentsID || obj.id === item.modulecomponentsId);

        if (component) {
            selectOptions.push({
                value: item.id,
                text: `${component.title} > ${item.title}`
            });
        }
    });

    return selectOptions;
});

// --- Lifecycle ---

onMounted(() => {
    /**
     * Contextual visibility guard: If this structure element's type
     * is not 'table', drop visibility from the orchestration form layer.
     */
    if (props.currentData?.type !== 'table' && props.field) {
        props.field.hidden = true;
    }
});
</script>

<template>
    <FormControlTypeSelect
        v-if="options && options.length"
        :current-data="currentData"
        :field="field"
        :form-id="formId"
        :options="options"
    />
</template>

<style scoped>
/* Presentation and operational properties inherit natively from the underlying type */
</style>